import { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config';
import { ToolConfig } from '@empathybroker/tsclient-play/dist/models/instance/tool-config';

import { Route } from 'vue-router';
import { mapActions, mapGetters } from 'vuex';

import { ApiEnum, CrudDataTypeEnum } from '../enums';

import { CrudFormMixin } from '../mixins/crud-form.mixin';

import { Filter, GuardResolveFunction, Pagination, RegularFilterConfig, Sort } from '../models';

import { ROUTER_CONSTS } from '../router/router.consts';

import { CONFIG_CONSTS } from '../store/config/config.consts';
import { CONTEXT_CONSTS } from '../store/context/context.consts';
import { CRUD_CONSTS } from '../store/crud/crud.consts';

export const CrudListMixin = {
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context
    }),
    ...mapGetters(CRUD_CONSTS.moduleName, {
      state: CRUD_CONSTS.getters.state
    }),
    data: function () {
      return ((this.state.dataType === this.dataType) ? this.state.data : []);
    },
    toolConfig: function (): ToolConfig {
      return CrudFormMixin.computed.toolConfig.bind(this)();
    },
    visiblePivotsConfig: function (): RegularFilterConfig[] {
      return (Object.values(this.context.filters).reduce((pivotsConfig: RegularFilterConfig[], filter: Filter) => {
        if (this.toolConfig.pivotIds.includes(filter.config.id)) {
          const filterConfig = (filter.config as RegularFilterConfig);
          if (filterConfig.values.length > 1) {
            pivotsConfig.push(filterConfig);
          }
        }
        return pivotsConfig;
      }, []) as RegularFilterConfig[]);
    },
    pivotsColumns: function (): any[] {
      return this.visiblePivotsConfig.map((pivotConfig: RegularFilterConfig) => ({
          id: this.getPivotColumnId(pivotConfig),
          text: (pivotConfig.prettyName || this.$epb.messages.filters.names[pivotConfig.id] || pivotConfig.id),
          sortable: true
        }
      ));
    },
    requestFilters: function () {}
  },
  data: function () {
    return {
      dataType: undefined,
      routes: {
        create: undefined,
        update: undefined
      },
      columns: [],
      sort: {},
      pagination: {},
      itemsPerPageOptions: [10, 20, 50],
      messages: {
        delete: {
          success: undefined
        },
        enable: {
          success: undefined
        },
        disable: {
          success: undefined
        }
      },
      query: undefined,
      strictMatching: true,
      selection: [],
      contextWatchDisabled: false
    };
  },
  props: {
    loading: Object
  },
  methods: {
    ...mapActions(CRUD_CONSTS.moduleName, {
      list: CRUD_CONSTS.actions.list,
      delete: CRUD_CONSTS.actions.delete,
      update: CRUD_CONSTS.actions.update,
      setEnabledAction: CRUD_CONSTS.actions.setEnabled
    }),
    loadData: async function () {
      this.$emit('updateLoading', { visible: true });
      try {
        await this.list({
          dataType: this.dataType,
          sort: this.sort,
          pagination: this.pagination,
          requestFilters: this.requestFilters,
          query: this.query,
          strictMatching: this.strictMatching
        });
        this.pagination.totalItems = this.state.pagination.totalItems;
        this.$emit('updateLoading', { visible: false });
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error, this.getApiEnum()));
        this.$emit('updateLoading', { visible: false });
      }
    },
    getPivotColumnId: function (pivotConfig: RegularFilterConfig): string {
      return (pivotConfig.toolingId || pivotConfig.id);
    },
    getPivotText: function (pivotConfig: RegularFilterConfig, dataItem): string {
      return this.getFilterText(pivotConfig.id, dataItem.context.pivots[this.getPivotColumnId(pivotConfig)]);
    },
    getFilterText: function (filterId: string, filterValues: (string | string[])): string {
      if (!Array.isArray(filterValues)) {
        filterValues = [filterValues];
      }
      return filterValues.map((filterValue: string) => {
        const filterValueConfig = this.context.filters[filterId].config.values
          .find((filterValueCfg: RegularFilterValueConfig) =>
            ((filterValueCfg.taggingId === filterValue) || (filterValueCfg.id === filterValue)));
        if (!!filterValueConfig) {
          const filterValuesMessages = this.$epb.messages.filters.values[filterId];
          return ((!!filterValueConfig && filterValueConfig.prettyName) ||
            (!!filterValuesMessages && filterValuesMessages[filterValueConfig.id]) ||
            filterValueConfig.id);
        }
        return filterValue;
      }).join(', ');
    },
    onQueryChange: function (query: string) {
      this.query = query;
      this.pagination.page = 1;
      this.loadData();
    },
    onSortChange: function (sort: Sort) {
      this.sort = sort;
      this.pagination.page = 1;
      this.loadData();
    },
    onPaginationChange: function (pagination: Pagination) {
      this.pagination = pagination;
      this.loadData();
    },
    onSelectionChange: function (selection: any[]) {
      this.selection = selection;
    },
    navigateToRoute: function (routeName: string) {
      this.$router.push({
        name: routeName,
        params: {
          ...this.$route.params,
          [ROUTER_CONSTS.params.id]: (this.selection.map((selectedItem: any) => selectedItem.id).join() || undefined)
        }
      });
    },
    onDelete: async function () {
      this.$emit('updateLoading', { visible: true });
      try {
        await this.delete({ dataType: this.dataType, ids: this.selection.map((selectedItem: any) => selectedItem.id) });
        this.$emit('showSnackbar', this.messages.delete.success);
        if (!this.data.length && (this.pagination.page > 1)) {
          this.pagination.page -= 1;
        }
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error, this.getApiEnum()));
        this.$emit('updateLoading', { visible: false });
      } finally {
        this.loadData();
      }
    },
    setEnabled: async function (enabled: boolean) {
      this.$emit('updateLoading', { visible: true });
      try {
        if (this.dataType === CrudDataTypeEnum.USERS) {
          await this.setEnabledAction({
            dataType: this.dataType,
            items: this.selection,
            enabled: enabled
          });
        } else {
          await this.update({
            dataType: this.dataType,
            items: this.selection.map((selectedItem: any) => ({
              ...selectedItem,
              enabled: enabled
            }))
          });
        }
        this.$emit('showSnackbar', ((enabled) ? this.messages.enable.success : this.messages.disable.success));
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error, this.getApiEnum()));
        this.$emit('updateLoading', { visible: false });
      } finally {
        this.loadData();
      }
    },
    getApiEnum: function (): ApiEnum {
      if (this.dataType === CrudDataTypeEnum.USERS) {
        return ApiEnum.USERS;
      }
      return ApiEnum.PLAY;
    }
  },
  created: function (): void {
    let page = this.state.pagination.page;
    if (!page || (this.state.dataType !== this.dataType)) {
      page = 1;
    }
    let itemsPerPage = this.state.pagination.itemsPerPage;
    if (!itemsPerPage || !this.itemsPerPageOptions.includes(itemsPerPage)) {
      itemsPerPage = this.itemsPerPageOptions[0];
    }
    this.pagination = {
      page: page,
      itemsPerPage: itemsPerPage,
      totalItems: 0
    };

    if (this.state.dataType === this.dataType) {
      this.sort = { ...this.state.sort };
    }
    this.loadData();
  },
  beforeRouteLeave: function (to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true;
    next();
  },
  watch: {
    context: {
      handler: function () {
        if (!this.contextWatchDisabled) {
          this.pagination.page = 1;
          this.loadData();
        }
      },
      deep: true
    },
    requestFilters: {
      handler: function () {
        this.loadData();
      },
      deep: true
    }
  }
};
